/**
 * Purchased Specific Trigger Component
 *
 * Trigger: "purchased specific [type]: [items]"
 * Shows: sellable type dropdown + items multi-select + Stripe plan options
 *
 * Note: The sellable type is stored in `reminderType`, and `stripePlan`
 * is an object with { id, nickname }.
 *
 * When this trigger is first selected, reminderType may be 'some'.
 * Once user picks a sellable type, reminderType becomes that type (e.g., 'workout_plan').
 */

import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { useSession } from 'src/contexts/SessionContext';
import { fetchAllStripePlans } from 'src/models/payment/payment';
import {
  FormSellableItemsMultiSelect,
  SellableType,
  getSellableTypeLabel,
  useSellableItems,
} from 'src/shared/Forms/Selects/SellableItemsMultiSelect';

import { TriggerComponentProps } from '../automationTriggers';
import { SellableTypeSelect } from '../components/SellableTypeSelect';
import { StripePlanFields } from '../components/StripePlanFields';

// Valid sellable types that support item selection
const SELLABLE_TYPES = ['workout_plan', 'group', 'product', 'package'];

export const PurchasedSpecificTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { user } = useSession();

  // sellable type is stored in reminderType
  // It can be 'some' (initial selection) or an actual type like 'workout_plan'
  const reminderType = automation.reminderType || '';
  const isSellableType = SELLABLE_TYPES.includes(reminderType);
  const sellableType = isSellableType ? (reminderType as SellableType) : null;

  const selectedItemIds = Array.isArray(automation.reminderTypeId)
    ? automation.reminderTypeId
    : automation.reminderTypeId
      ? [automation.reminderTypeId]
      : [];

  // Fetch items for readonly display (only when we have a valid sellable type)
  const { data: items = [] } = useSellableItems(sellableType);
  const selectedItems = items.filter((item) =>
    selectedItemIds.some((id) => String(id) === item.value)
  );

  // Fetch Stripe plans for readonly display
  const { data: stripePlans = [] } = useQuery({
    queryKey: ['stripePlans', user?.id],
    queryFn: () => fetchAllStripePlans(),
    enabled: readOnly && !!automation.stripePlan?.id,
    staleTime: 60000,
  });

  if (readOnly) {
    const typeLabel = sellableType
      ? getSellableTypeLabel(sellableType)
      : 'items';

    const parts: string[] = [];
    parts.push(`purchased specific ${typeLabel}`);

    // Add item names if selected
    if (selectedItems.length > 0) {
      const itemNames = selectedItems.map((item) => item.label).join(', ');
      parts.push(`: ${itemNames}`);
    }

    // Add payment plan if selected (stripePlan is { id, nickname })
    if (automation.stripePlan?.id) {
      const plan = stripePlans.find((p) => p.id === automation.stripePlan?.id);
      if (plan) {
        parts.push(`with Payment Plan: ${plan.name}`);
      } else if (automation.stripePlan.nickname) {
        parts.push(`with Payment Plan: ${automation.stripePlan.nickname}`);
      }
    }

    if (automation.afterTrial) {
      parts.push('(after trial)');
    }

    if (automation.everyPayment) {
      parts.push('(every payment)');
    }

    return <span>{parts.join(' ')}</span>;
  }

  // Edit mode
  // Always show the sellable type dropdown (it's how user selects what type of purchase)
  return (
    <Stack spacing={2}>
      <SellableTypeSelect index={index} />
      {sellableType && (
        <Box sx={{ maxWidth: 400 }}>
          <FormSellableItemsMultiSelect
            name={`automations.${index}.reminderTypeId`}
            sellableType={sellableType}
          />
        </Box>
      )}
      <StripePlanFields automation={automation} index={index} />
    </Stack>
  );
};
